'use client';

import { ButtonProjection } from '@lib/sanity/types/common';
import { HeaderProps } from '@lib/types';
import Anchor from '@ui/Anchor/Anchor';
import { Button, buttonVariants } from '@ui/Button/Button';
import cn from '@utils/cn';
import { isButtonExist } from '@utils/validations';
import { VariantProps } from 'class-variance-authority';
import dynamic from 'next/dynamic';

const EmbedButton = dynamic(() => import('../../../../ui/EmbedButton/EmbedButton'), { ssr: false });

type ButtonsProps = Pick<
  HeaderProps,
  'embedButton' | 'buttonPrimary' | 'buttonSecondary' | 'buttonLink' | 'className'
> & {
  buttonClassName?: string;
  size?: 'link' | 's' | 'xl' | 'l' | 'm' | 'xs' | 'icon' | undefined;
};

export default function HeaderButtons({
  embedButton,
  buttonPrimary,
  buttonSecondary,
  buttonLink,
  className,
  buttonClassName,
  size,
}: ButtonsProps) {
  if (!isButtonExist(buttonPrimary) && !isButtonExist(buttonSecondary) && !isButtonExist(buttonLink) && !embedButton) {
    return null;
  }

  if (embedButton) {
    return (
      <div className={className ?? 'flex w-full flex-col gap-4 sm:flex-row'}>
        <EmbedButton embedButton={embedButton} />;
      </div>
    );
  }

  return (
    <div className={className ?? 'flex w-full flex-col gap-4 sm:flex-row'}>
      <HeaderButton className={buttonClassName} button={buttonPrimary} size={size || 'l'} />
      <HeaderButton className={buttonClassName} button={buttonSecondary} variant="outline" size={size || 'l'} />
      <HeaderButton className={buttonClassName} button={buttonLink} variant="link" size="link" />
    </div>
  );
}

type HeaderButtonProps = {
  size: 'link' | 's' | 'xl' | 'l' | 'm' | 'xs' | 'icon' | undefined;
  button?: ButtonProjection;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  className?: string;
};

function HeaderButton({ button, size, variant, className }: HeaderButtonProps) {
  if (!isButtonExist(button)) {
    return null;
  }

  const text = button.text?.value ?? '';
  const href = button.url ?? undefined;

  return (
    <Button className={cn('select-none', className)} aria-label={text} size={size} variant={variant} asChild>
      <Anchor
        {...(button.eventTracking?.elementName && {
          'ga-element': button.eventTracking.elementName,
        })}
        {...(button.eventTracking?.dataSource && {
          'ga-source': button.eventTracking.dataSource,
        })}
        {...(button.eventTracking?.dataMedium && {
          'ga-medium': button.eventTracking.dataMedium,
        })}
        {...(button.eventTracking?.dataExperiment && {
          'ga-experiment': button.eventTracking.dataExperiment,
        })}
        {...(button.eventTracking?.dataVariant && {
          'ga-variant': button.eventTracking.dataVariant,
        })}
        aria-label={text}
        href={href}
      >
        {text}
      </Anchor>
    </Button>
  );
}
